import React, { useState, useEffect } from 'react';
import { BoilerElement, HeatingType } from '../types';
import PCDBSearch, { PCDBHeatingSystem } from './PCDBSearch';
import { X } from 'lucide-react';

interface BoilerInfoPromptProps {
  isOpen: boolean;
  boilerElement: BoilerElement | null;
  onSave: (details: { heatingType: HeatingType; brand: string; model: string; gcNumber: string; pcdbSystem?: PCDBHeatingSystem; }) => void;
  onClose: () => void;
  isFullscreen?: boolean;
  onPCDBSystemSync?: (pcdbSystem: PCDBHeatingSystem) => void;
}

const BoilerInfoPrompt: React.FC<BoilerInfoPromptProps> = ({ 
  isOpen, 
  boilerElement, 
  onSave, 
  onClose, 
  isFullscreen = false,
  onPCDBSystemSync
}) => {
  const [selectedPCDBSystem, setSelectedPCDBSystem] = useState<PCDBHeatingSystem | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false); // Default to PCDB search
  const [heatingType, setHeatingType] = useState<HeatingType>('combi');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [gcNumber, setGcNumber] = useState('');

  useEffect(() => {
    if (boilerElement) {
      setHeatingType(boilerElement.heatingType || 'combi');
      setBrand(boilerElement.brand || '');
      setModel(boilerElement.model || '');
      setGcNumber(boilerElement.gcNumber || '');
      setSelectedPCDBSystem(null);
      setIsManualEntry(false); // Reset to PCDB search when opening
    }
  }, [boilerElement]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    if (selectedPCDBSystem && !isManualEntry) {
      // Use PCDB system data
      onSave({
        heatingType: selectedPCDBSystem.installation_type as HeatingType,
        brand: selectedPCDBSystem.brand,
        model: selectedPCDBSystem.model,
        gcNumber: selectedPCDBSystem.pcdb_reference || '',
        pcdbSystem: selectedPCDBSystem
      });
      
      // Also sync to SurveyorSiteNotes if callback is provided
      if (onPCDBSystemSync) {
        onPCDBSystemSync(selectedPCDBSystem);
      }
    } else {
      // Use manual entry data
      onSave({ heatingType, brand, model, gcNumber });
    }
    onClose();
  };

  const handlePCDBSystemSelect = (system: PCDBHeatingSystem | null) => {
    setSelectedPCDBSystem(system);
    if (system && !isManualEntry) {
      // Auto-populate manual fields from PCDB system
      setHeatingType(system.installation_type as HeatingType);
      setBrand(system.brand);
      setModel(system.model);
      setGcNumber(system.pcdb_reference || '');
    }
  };

  const handleManualEntryToggle = (manual: boolean) => {
    setIsManualEntry(manual);
    if (manual) {
      // Clear PCDB selection when switching to manual
      setSelectedPCDBSystem(null);
    } else {
      // Clear manual fields when switching to PCDB
      setHeatingType('combi');
      setBrand('');
      setModel('');
      setGcNumber('');
    }
  };

  const canSave = isManualEntry 
    ? (brand.trim() && model.trim()) 
    : selectedPCDBSystem !== null;

  return (
    <div className={`fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 ${isFullscreen ? 'z-[10003]' : 'z-[1000]'}`}>
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/20">
        {/* Modern Header with Gradient */}
        <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Boiler Details</h3>
                <p className="text-blue-100 text-xs">Search database or enter manually</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="bg-white/20 text-white px-2.5 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm">
                Floor {boilerElement?.floor}
              </span>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          {/* Decorative gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-white/5 to-purple-600/0 pointer-events-none"></div>
        </div>

        {/* Content Area */}
        <div className="px-5 py-5 space-y-5 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Input Method Toggle */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Input Method
            </h4>
            <div className="flex gap-3">
              <button
                onClick={() => handleManualEntryToggle(false)}
                className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                  !isManualEntry
                    ? 'bg-blue-100 border-blue-400 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 1.79 4 4 4h8c2.21 0 4-1.79 4-4V7c0-2.21-1.79-4-4-4H8c-2.21 0-4 1.79-4 4z" />
                  </svg>
                  <span>PCDB Database</span>
                </div>
              </button>
              <button
                onClick={() => handleManualEntryToggle(true)}
                className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all duration-200 ${
                  isManualEntry
                    ? 'bg-blue-100 border-blue-400 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span>Manual Entry</span>
                </div>
              </button>
            </div>
          </div>

          {/* PCDB Search Section */}
          {!isManualEntry && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                PCDB Heating System Search
              </h4>
              <PCDBSearch
                onSelect={handlePCDBSystemSelect}
                selectedSystem={selectedPCDBSystem}
                placeholder="Search by brand, model, or PCDB reference..."
              />
              {selectedPCDBSystem && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-semibold text-green-800">PCDB System Selected</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <p><strong>Brand:</strong> {selectedPCDBSystem.brand}</p>
                    <p><strong>Model:</strong> {selectedPCDBSystem.model}</p>
                    <p><strong>Type:</strong> {selectedPCDBSystem.installation_type}</p>
                    {selectedPCDBSystem.pcdb_reference && (
                      <p><strong>PCDB Ref:</strong> {selectedPCDBSystem.pcdb_reference}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manual Entry Section */}
          {isManualEntry && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Manual Entry
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Heating Type</label>
                  <select 
                    value={heatingType} 
                    onChange={(e) => setHeatingType(e.target.value as HeatingType)}
                    className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  >
                    <option value="combi">Combi</option>
                    <option value="system">System</option>
                    <option value="regular">Regular</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Brand</label>
                    <input
                      type="text"
                      value={brand}
                      onChange={(e) => setBrand(e.target.value)}
                      placeholder="e.g., Worcester, Vaillant"
                      className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Model</label>
                    <input
                      type="text"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      placeholder="e.g., Greenstar 30CDi"
                      className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">GC Number / Reference</label>
                  <input
                    type="text"
                    value={gcNumber}
                    onChange={(e) => setGcNumber(e.target.value)}
                    placeholder="e.g., 41-393-01"
                    className="w-full px-3 py-2.5 text-sm bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modern Action Bar */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex justify-end items-center space-x-2">
            <button
              onClick={onClose}
              className="px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/20 transition-all duration-200"
            >
              Skip
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg ${
                canSave
                  ? 'text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl hover:scale-105 ring-2 ring-blue-200/50'
                  : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Save Boiler</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoilerInfoPrompt; 